import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiEdit, FiEye, FiPlus, FiSearch, FiTag } from 'react-icons/fi';
import {
  disableClientPortalAccount,
  enableClientPortalAccount,
  getClients,
  resendClientPortalPassword,
} from '../api';
import EditClientModal from '../components/EditClientModal';
import ClientQuotationCategoriesEditor from '../components/ClientQuotationCategoriesEditor';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';

const ClientListPage = () => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingClientId, setEditingClientId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const { user } = useCurrentUser();
  const canCreateClient = hasPermission(user, 'client.create');
  const canUpdateClient = hasPermission(user, 'client.update');
  const canReadClient = hasPermission(user, 'client.read');
  const canManageClientPricing = hasPermission(user, 'client_pricing.manage');
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [accountActionByClient, setAccountActionByClient] = useState({});
  const [accountNotice, setAccountNotice] = useState('');
  const pageSize = 12;
  const columnCount = canManageClientPricing ? 6 : 5;

  const fetchClients = async () => {
    setLoading(true);
    setError('');
    try {
      const trimmedSearch = searchTerm.trim();
      const data = await getClients({
        page,
        pageSize,
        ...(trimmedSearch ? { search: trimmedSearch } : {}),
      });
      const normalized = normalizePaginatedResponse(data);
      setClients(normalized.data || []);
      setTotalClients(normalized.total || 0);
      setTotalPages(
        normalized.totalPages || Math.max(1, Math.ceil((normalized.total || 0) / pageSize))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [page, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleEditClient = (client) => {
    if (!canUpdateClient) return;
    setEditingClientId(client.id);
  };

  const handleModalClose = () => {
    setEditingClientId(null);
  };

  const handleClientUpdated = async () => {
    await fetchClients();
  };

  const mergeClient = (updatedClient) => {
    if (!updatedClient?.id) return;
    setClients((prev) => prev.map((client) => (
      client.id === updatedClient.id ? { ...client, ...updatedClient } : client
    )));
  };

  const runAccountAction = async (client, action) => {
    if (!canUpdateClient || !client?.id) return;
    setAccountNotice('');
    setAccountActionByClient((prev) => ({ ...prev, [client.id]: action }));
    try {
      let response;
      if (action === 'enable') {
        response = await enableClientPortalAccount(client.id);
        setAccountNotice('Acceso habilitado y contraseña temporal enviada.');
      } else if (action === 'resend') {
        response = await resendClientPortalPassword(client.id);
        setAccountNotice('Nueva contraseña temporal enviada.');
      } else if (action === 'disable') {
        response = await disableClientPortalAccount(client.id);
        setAccountNotice('Acceso del portal deshabilitado.');
      }
      mergeClient(response?.client);
    } catch (err) {
      setAccountNotice(err?.response?.data?.message || err?.message || 'No se pudo actualizar el acceso del cliente.');
    } finally {
      setAccountActionByClient((prev) => {
        const next = { ...prev };
        delete next[client.id];
        return next;
      });
    }
  };

  const toggleClientDetails = (clientId) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  return (
    <>
      <main className="record-list-page">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <section className="record-list-panel" aria-label="Gestión de clientes">
            <div className="record-list-header">
              <h1 className="record-list-title">Clientes</h1>
              {canCreateClient && (
                <Link to="/client/new" className="record-list-header-action">
                  <FiPlus className="h-4 w-4" aria-hidden="true" />
                  Nuevo Cliente
                </Link>
              )}
            </div>
            <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
              <label className="relative block min-w-0 flex-1">
                <span className="sr-only">Buscar clientes por nombre, RUT, giro o correo</span>
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                <input
                  type="search"
                  placeholder="Buscar por nombre, rut, giro o correo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="record-list-input pl-9 pr-3"
                />
              </label>
              <div className="flex shrink-0 items-center gap-3 text-xs font-medium text-slate-500">
                <span><strong className="font-bold text-slate-900">{totalClients}</strong> clientes</span>
                <span className="border-l border-slate-200 pl-3">{clients.length} en página</span>
              </div>
            </div>
          </section>

          {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}
          {accountNotice && (
            <p className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              {accountNotice}
            </p>
          )}

          <div className="record-list-scroll" role="region" aria-label="Tabla de clientes" tabIndex={0}>
            <table className="record-list-table min-w-[1000px]" aria-label="Lista de clientes" aria-busy={loading}>
              <thead>
                <tr>
                  <th scope="col" className="font-bold">Cliente</th>
                  <th scope="col" className="font-bold">RUT</th>
                  <th scope="col" className="font-bold">Contacto</th>
                  <th scope="col" className="font-bold">Portal</th>
                  {canManageClientPricing && (
                    <th scope="col" className="font-bold">Descuento</th>
                  )}
                  <th scope="col" className="text-right font-bold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columnCount} className="px-4 py-8 text-center text-slate-600">
                      <InlineLoading label="Cargando clientes" />
                    </td>
                  </tr>
                ) : clients.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className="px-4 py-8 text-center text-slate-600">
                      No hay clientes para mostrar.
                    </td>
                  </tr>
                ) : (
                  clients.map((client) => {
                    const clientName = client.name || 'Sin nombre';
                    const isExpanded = expandedClients.has(client.id);
                    const detailsId = `client-${client.id}-details`;
                    const pricingCategoryName = client.clientPricingLabel || client.PricingCategory?.name || '';
                    return (
                      <React.Fragment key={client.id}>
                        <tr className="record-list-row" data-expanded={isExpanded}>
                          <th scope="row" className="font-bold text-slate-950">
                            <button
                              type="button"
                              onClick={() => toggleClientDetails(client.id)}
                              className="flex w-48 items-center gap-2 rounded-md text-left outline-none hover:text-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500"
                              aria-label={`${isExpanded ? 'Ocultar' : 'Ver'} más de ${clientName}`}
                              aria-expanded={isExpanded}
                              aria-controls={detailsId}
                            >
                              <FiChevronDown className={`shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} aria-hidden="true" />
                              <span className="min-w-0 flex-1 break-words">{clientName}</span>
                            </button>
                          </th>
                          <td className="whitespace-nowrap text-slate-600">{client.rut || '—'}</td>
                          <td className="text-slate-700">
                            <div className="max-w-xs break-words">{client.email || '—'}</div>
                            <div className="mt-1 whitespace-nowrap text-xs text-slate-500">{client.phone || '—'}</div>
                          </td>
                          <td>
                            <div className="flex flex-col items-start gap-1">
                              <span className={`record-list-badge whitespace-nowrap ${
                                client.account_available
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                  : 'border-slate-200 bg-slate-100 text-slate-600'
                              }`}>
                                {client.account_available ? 'Portal activo' : 'Portal inactivo'}
                              </span>
                              {client.mustChangePassword && (
                                <span className="record-list-badge border-amber-200 bg-amber-50 text-amber-800">
                                  Cambio de clave pendiente
                                </span>
                              )}
                            </div>
                          </td>
                          {canManageClientPricing && (
                            <td>
                              <span className={`record-list-badge ${
                                pricingCategoryName
                                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                                  : 'border-slate-200 bg-slate-100 text-slate-600'
                              }`}>
                                <FiTag className="shrink-0" aria-hidden="true" />
                                {pricingCategoryName || 'Sin descuento'}
                              </span>
                            </td>
                          )}
                          <td>
                            <div className="flex justify-end gap-2">
                              {canUpdateClient && (
                                <button
                                  type="button"
                                  onClick={() => handleEditClient(client)}
                                  title="Editar cliente"
                                  aria-label={`Editar ${clientName}`}
                                  className="record-list-action record-list-action-primary h-8 w-8 p-0"
                                >
                                  <FiEdit />
                                </button>
                              )}
                              {canReadClient && (
                                <Link
                                  to={`/client/${client.id}`}
                                  title="Ver detalle"
                                  aria-label={`Ver detalle de ${clientName}`}
                                  className="record-list-action h-8 w-8 p-0"
                                >
                                  <FiEye />
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr id={detailsId} className="record-list-details">
                            <td colSpan={columnCount} className="px-4 py-4">
                              <div className="space-y-4 text-sm text-slate-700">
                                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                  <div>
                                    <dt className="text-xs font-medium text-slate-500">Giro</dt>
                                    <dd className="mt-1">{client.giro || '—'}</dd>
                                  </div>
                                  <div>
                                    <dt className="text-xs font-medium text-slate-500">Dirección</dt>
                                    <dd className="mt-1">{client.address || '—'}</dd>
                                  </div>
                                  <div>
                                    <dt className="text-xs font-medium text-slate-500">Ciudad</dt>
                                    <dd className="mt-1">{client.city || '—'}</dd>
                                  </div>
                                </dl>
                                {client.account_available && (
                                  <ClientQuotationCategoriesEditor
                                    clientId={client.id}
                                    enabled={client.account_available}
                                    canEdit={canUpdateClient}
                                    compact
                                  />
                                )}
                                {canUpdateClient && (
                                  <div className="mt-3 rounded-lg border border-slate-200 bg-white/80 p-3">
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                      <div>
                                        <p className="text-sm font-semibold text-slate-800">Portal de cliente</p>
                                        <p className="text-xs text-slate-500">
                                          Gestiona acceso y contrasenas temporales del portal.
                                        </p>
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {!client.account_available ? (
                                          <button
                                            type="button"
                                            onClick={() => runAccountAction(client, 'enable')}
                                            disabled={!!accountActionByClient[client.id]}
                                            className="record-list-action record-list-action-primary"
                                          >
                                            {accountActionByClient[client.id] === 'enable' ? 'Enviando...' : 'Habilitar portal'}
                                          </button>
                                        ) : (
                                          <>
                                            <button
                                              type="button"
                                              onClick={() => runAccountAction(client, 'resend')}
                                              disabled={!!accountActionByClient[client.id]}
                                              className="record-list-action record-list-action-primary"
                                            >
                                              {accountActionByClient[client.id] === 'resend' ? 'Enviando...' : 'Reenviar clave'}
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => runAccountAction(client, 'disable')}
                                              disabled={!!accountActionByClient[client.id]}
                                              className="record-list-action"
                                            >
                                              {accountActionByClient[client.id] === 'disable' ? 'Guardando...' : 'Deshabilitar portal'}
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalClients > pageSize && (
            <div className="record-list-pagination">
              <div>
                Mostrando {totalClients === 0 ? 0 : (page - 1) * pageSize + 1}-
                {Math.min(page * pageSize, totalClients)} de {totalClients}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className="record-list-action"
                >
                  Anterior
                </button>
                <span>
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page >= totalPages}
                  className="record-list-action"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {editingClientId && (
        <EditClientModal
          clientId={editingClientId}
          onClose={handleModalClose}
          onUpdated={handleClientUpdated}
        />
      )}
    </>
  );
};

export default ClientListPage;
